/**
 * Market data hooks — typed TanStack Query wrappers for market exploration.
 *
 * Replaces: js/modules/explorer.js (399 lines)
 * Backend:  backend/market/interfaces.py + backend/data/interfaces.py
 */

import { useQuery } from "@tanstack/react-query";

import { apiGet } from "@/lib/api-client";
import type { WatchlistItem } from "@/sdk/types/data";

export const MARKET_DATA_QUERY_KEYS = {
  search: (q: string) => ["markets", "search", q] as const,
  trending: ["markets", "trending"] as const,
  detail: (id: string) => ["markets", "detail", id] as const,
  watchlist: ["watchlist"] as const,
};

export function useMarketSearch(query: string, enabled = true) {
  // Backend: GET /api/markets/explore?search={query} (market_explorer router).
  //          /api/explorer/search does not exist.
  return useQuery({
    queryKey: MARKET_DATA_QUERY_KEYS.search(query),
    queryFn: () =>
      apiGet<{ markets: unknown[] }>(
        `/api/markets/explore?search=${encodeURIComponent(query)}`,
      ),
    enabled: enabled && query.length > 0,
  });
}

export function useTrendingMarkets(refetchInterval = 60_000) {
  return useQuery({
    queryKey: MARKET_DATA_QUERY_KEYS.trending,
    queryFn: () =>
      apiGet<{ markets: unknown[] }>("/api/explorer/trending"),
    refetchInterval,
  });
}

export function useMarketDetail(marketId: string | null) {
  return useQuery({
    queryKey: MARKET_DATA_QUERY_KEYS.detail(marketId ?? ""),
    queryFn: () =>
      apiGet<Record<string, unknown>>(
        `/api/polymarket/markets/${marketId}`,
      ),
    enabled: !!marketId,
  });
}

export function useWatchlist(refetchInterval = 30_000) {
  return useQuery({
    queryKey: MARKET_DATA_QUERY_KEYS.watchlist,
    queryFn: () =>
      apiGet<{ items: WatchlistItem[] }>("/api/watchlist"),
    refetchInterval,
  });
}
